/**
 * ShopMobileBottomBar — sticky Bottom-Navigation für Mobile (< md).
 *
 * Design-Muster: iOS/Instagram/WhatsApp-Style Tab-Bar. Vier Kern-Aktionen
 * (Home, Suche, Warenkorb, Konto) immer per Daumen erreichbar am unteren
 * Rand. Konvertiert nachweislich besser auf Mobile als Aktion-Icons im
 * Top-Header — weil sie in der natürlichen Griff-Zone bleiben.
 *
 * Verhalten:
 * - Nur auf < md (< 768px) sichtbar — Desktop hat den Top-Header
 * - Wird auf Admin-Routen NICHT gerendert (Admin hat eigene UX)
 * - Cart-Badge zeigt Anzahl bei > 0
 * - Aktiver Tab-Highlight aus URL abgeleitet
 * - iOS-Safe-Area-Padding unten (kein Überlappen mit Home-Indicator)
 * - Höhe wird als CSS-Variable `--mobile-bottom-bar-h` gesetzt, damit
 *   nachfolgende Content-Bereiche unten Padding bekommen können.
 *
 * Nicht getrackt: Suche öffnet globales SearchOverlay via CustomEvent —
 * das Overlay ist schon im Header gemountet, wir feuern nur ein Event.
 */
import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, Search, ShoppingBag, UserRound } from 'lucide-react';
import { useCart } from './CartContext';
import SearchOverlay from './SearchOverlay';

// CSS-Variable für Content-Padding
const BAR_HEIGHT_PX = 64;

export default function ShopMobileBottomBar() {
  const location = useLocation();
  const { cartCount, setCartOpen } = useCart();
  const [searchOpen, setSearchOpen] = React.useState(false);
  const [loggedIn, setLoggedIn] = React.useState(false);

  // Auf Admin-Routen NICHT rendern
  const path = location.pathname || '/';
  const isAdmin = path.startsWith('/admin') || path.startsWith('/admin221542');
  const isCheckout = path.startsWith('/checkout');

  React.useEffect(() => {
    // CSS-Variable setzen, damit Bottom-Content-Padding klappt
    if (!isAdmin && !isCheckout) {
      document.documentElement.style.setProperty('--mobile-bottom-bar-h', `${BAR_HEIGHT_PX}px`);
    } else {
      document.documentElement.style.setProperty('--mobile-bottom-bar-h', '0px');
    }
  }, [isAdmin, isCheckout]);

  // Login-Status aus localStorage lesen (leichtgewichtiger Check)
  React.useEffect(() => {
    const check = () => setLoggedIn(!!localStorage.getItem('shop_user_token') || !!localStorage.getItem('customer_token'));
    check();
    window.addEventListener('storage', check);
    return () => window.removeEventListener('storage', check);
  }, []);

  if (isAdmin || isCheckout) return null;

  const isActive = (pattern) => {
    if (pattern === '/') return path === '/' || path === '/shop' || path === '/shop-katalog';
    return path.startsWith(pattern);
  };

  const konto_target = loggedIn ? '/kundenkonto/dashboard' : '/kundenkonto';

  return (
    <>
      {/* Padding-Spacer damit letzter Content nicht unter der Bar liegt */}
      <div className="md:hidden" style={{ height: `${BAR_HEIGHT_PX}px` }} aria-hidden="true" />

      <nav
        className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-t border-stone-200 shadow-[0_-2px_16px_rgba(0,0,0,0.06)]"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        aria-label="Hauptnavigation Mobil"
        data-testid="mobile-bottom-bar"
      >
        <div className="grid grid-cols-4 h-16">
          <BottomTab
            to="/"
            icon={Home}
            label="Start"
            active={isActive('/')}
            testId="mobile-bottom-home"
          />
          <BottomTabButton
            onClick={() => setSearchOpen(true)}
            icon={Search}
            label="Suche"
            active={false}
            testId="mobile-bottom-search"
          />
          <BottomTabButton
            onClick={() => setCartOpen(true)}
            icon={ShoppingBag}
            label="Warenkorb"
            badge={cartCount > 0 ? cartCount : null}
            active={false}
            testId="mobile-bottom-cart"
          />
          <BottomTab
            to={konto_target}
            icon={UserRound}
            label={loggedIn ? 'Konto' : 'Login'}
            active={isActive('/kundenkonto')}
            testId="mobile-bottom-account"
          />
        </div>
      </nav>

      <SearchOverlay open={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}

function BottomTab({ to, icon: Icon, label, active, testId, badge }) {
  return (
    <Link
      to={to}
      className={`flex flex-col items-center justify-center gap-0.5 relative transition-colors ${
        active ? 'text-[#8a6d10]' : 'text-stone-600 hover:text-stone-900'
      }`}
      data-testid={testId}
    >
      <div className="relative">
        <Icon className="w-5 h-5" />
        {badge != null && (
          <span className="absolute -top-1.5 -right-2 min-w-4 h-4 px-1 rounded-full bg-[#C9A23C] text-white text-[10px] font-bold flex items-center justify-center">
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </div>
      <span className="text-[10px] font-medium leading-tight">{label}</span>
      {active && <span className="absolute top-0 h-0.5 w-8 rounded-full bg-[#C9A23C]" />}
    </Link>
  );
}

function BottomTabButton({ onClick, icon: Icon, label, badge, active, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex flex-col items-center justify-center gap-0.5 relative transition-colors ${
        active ? 'text-[#8a6d10]' : 'text-stone-600 hover:text-stone-900'
      }`}
      data-testid={testId}
    >
      <div className="relative">
        <Icon className="w-5 h-5" />
        {badge != null && (
          <span className="absolute -top-1.5 -right-2 min-w-4 h-4 px-1 rounded-full bg-[#C9A23C] text-white text-[10px] font-bold flex items-center justify-center">
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </div>
      <span className="text-[10px] font-medium leading-tight">{label}</span>
    </button>
  );
}
